import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Importa tus funciones del servicio de Gemini
import { getCoreIdeaFromText, createQuestionFromDesire } from '../../services/geminiService';

const SevenWhys = () => {
  const [initialDesire, setInitialDesire] = useState('');
  const [currentStep, setCurrentStep] = useState(0); // 0: initial input, 1-7: questions, 8: complete
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false); // Used for loading states
  const [error, setError] = useState('');

  // Animation for the component itself
  const { ref: componentRef, inView: componentInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const handleStartExercise = async () => {
    if (!initialDesire.trim()) {
      setError('Por favor, ingresa tu deseo para comenzar.');
      return;
    }
    setError('');
    setIsProcessing(true);
    try {
      const firstQuestion = await createQuestionFromDesire(initialDesire);

      setCurrentQuestion(firstQuestion);
      setQuestionsAndAnswers([{ question: firstQuestion, answer: '' }]);
      setCurrentStep(1);
    } catch (err) {
      console.error("Error al iniciar el ejercicio con Gemini (createQuestionFromDesire):", err);
      setError('Hubo un problema al generar la primera pregunta. Inténtalo de nuevo.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!currentAnswer.trim()) {
      setError('Por favor, ingresa tu respuesta.');
      return;
    }
    setError('');
    setIsProcessing(true);

    const updatedQnA = [...questionsAndAnswers];
    updatedQnA[currentStep - 1].answer = currentAnswer;
    setQuestionsAndAnswers(updatedQnA);
    setCurrentAnswer(''); // Clear current answer input

    if (currentStep < 7) {
      try {
        const previousResponses = updatedQnA.filter(qa => qa.answer.trim() !== '').map(qa => qa.answer);
        const coreIdea = await getCoreIdeaFromText(currentAnswer, previousResponses);

        const nextQuestion = `Entendido, ¿y por qué es importante para ti ${coreIdea}?`;

        setCurrentQuestion(nextQuestion);
        setQuestionsAndAnswers([...updatedQnA, { question: nextQuestion, answer: '' }]);
        setCurrentStep(currentStep + 1);
      } catch (err) {
        console.error("Error al generar la siguiente pregunta con Gemini (getCoreIdeaFromText):", err);
        setError('Hubo un problema al generar la siguiente pregunta. Inténtalo de nuevo.');
      } finally {
        setIsProcessing(false);
      }
    } else {
      setCurrentStep(8);
      setCurrentQuestion('');
      setIsProcessing(false);
    }
  };

  const handlePrintPdf = async () => {
    setIsProcessing(true);
    const input = document.getElementById('qna-content');

    if (input) {
      // Guarda los estilos originales para restaurarlos después
      const originalBodyBg = document.body.style.backgroundColor;
      const originalInputBg = input.style.backgroundColor;
      const originalInputColor = input.style.color;

      const originalQuestionColors = [];
      const originalAnswerColors = [];

      // Aplica estilos temporales para el PDF
      document.body.style.backgroundColor = '#ffffff'; // Fondo blanco para el body
      input.style.backgroundColor = '#ffffff'; // Fondo blanco para el contenedor QnA
      input.style.color = '#000000'; // Texto negro por defecto para el contenedor QnA

      // Recorre todas las preguntas y respuestas para asegurar texto negro
      const questionElements = input.querySelectorAll('.question-text');
      const answerElements = input.querySelectorAll('.answer-text');

      questionElements.forEach(el => {
        originalQuestionColors.push(el.style.color);
        el.style.color = '#333333'; // Gris oscuro para las preguntas
      });
      answerElements.forEach(el => {
        originalAnswerColors.push(el.style.color);
        el.style.color = '#555555'; // Gris un poco más claro para las respuestas
      });

      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff', // Asegura el fondo blanco del canvas
        windowWidth: input.scrollWidth,
        windowHeight: input.scrollHeight,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps= pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      const margin = 10;
      let heightLeft = pdfHeight;
      let yOffset = 0;
      const pageHeight = pdf.internal.pageSize.getHeight();

      while(heightLeft > 0) {
          if (yOffset > 0) {
              pdf.addPage();
          }
          pdf.addImage(imgData, 'PNG', margin, margin - yOffset, pdfWidth - (2*margin), pdfHeight);
          heightLeft -= (pageHeight - (2*margin));
          yOffset += (pageHeight - (2*margin));
      }

      pdf.save(`Mis_7_Porques_${initialDesire.replace(/[^a-zA-Z0-9_]/g, '')}.pdf`);

      // Restaura los estilos originales
      document.body.style.backgroundColor = originalBodyBg;
      input.style.backgroundColor = originalInputBg;
      input.style.color = originalInputColor;

      questionElements.forEach((el, index) => {
        el.style.color = originalQuestionColors[index];
      });
      answerElements.forEach((el, index) => {
        el.style.color = originalAnswerColors[index];
      });
    }
    setIsProcessing(false);
  };

  return (
    // Contenedor principal de la página, con el mismo fondo gradiente de HomePage
    <div className="bg-black text-white min-h-screen font-sans flex flex-col items-center">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95"></div>

      <main className="relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center">
        {/* Contenedor del componente "Los 7 Porqués" */}
        <div ref={componentRef}
          className={`w-full max-w-2xl mx-auto px-4 py-8 bg-gray-900 rounded-xl shadow-lg border border-gray-700
            transition-all duration-700 ease-in-out
            ${componentInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}
          `}
        >
          <h2 className="text-3xl font-bold text-white text-center mb-6">Los 7 Porqués</h2>
          <p className="text-gray-300 text-center mb-8">
            Profundiza en tu deseo de manifestación respondiendo a 7 preguntas clave generadas por IA. Descubre tu verdadera motivación.
          </p>

          {error && <p className="text-red-400 text-center mb-4 text-sm">{error}</p>}

          {currentStep === 0 && (
            <div className="flex flex-col items-center">
              <input
                type="text"
                value={initialDesire}
                onChange={(e) => setInitialDesire(e.target.value)}
                placeholder="Escribe aquí lo que quieres manifestar (ej: Abundancia financiera)"
                className="w-full p-3 mb-4 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={isProcessing}
              />
              <button
                onClick={handleStartExercise}
                className="w-full py-3 px-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg shadow-md hover:from-purple-600 hover:to-pink-600 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isProcessing || !initialDesire.trim()}
              >
                {isProcessing ? 'Generando pregunta...' : 'Subir Deseo'}
              </button>
            </div>
          )}

          {(currentStep > 0 && currentStep <= 7) && (
            <div className="flex flex-col items-center animate-fade-in-up">
              <p className="text-lg text-gray-200 text-center mb-4 font-medium">
                <span className="font-bold text-purple-400">Paso {currentStep}/7:</span> {currentQuestion}
              </p>
              <textarea
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(e.target.value)}
                placeholder="Escribe tu respuesta aquí..."
                rows="4"
                className="w-full p-3 mb-4 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                disabled={isProcessing}
              ></textarea>
              <button
                onClick={handleSubmitAnswer}
                className="w-full py-3 px-6 bg-gradient-to-r from-sky-400 to-teal-500 text-white font-semibold rounded-lg shadow-md hover:from-sky-500 hover:to-teal-600 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isProcessing || !currentAnswer.trim()}
              >
                {isProcessing ? 'Enviando respuesta...' : 'Responder'}
              </button>
            </div>
          )}

          {currentStep === 8 && (
            <div className="flex flex-col items-center animate-fade-in-up">
              <h3 className="text-2xl font-bold text-purple-400 text-center mb-6">¡Ejercicio Completado!</h3>
              <p className="text-gray-300 text-center mb-8">
                Aquí tienes tus 7 porqués. Guarda este documento para recordar tu verdadera motivación.
              </p>

              <div id="qna-content" className="w-full p-4 bg-gray-900 rounded-lg border border-gray-700 text-white mb-6">
                <h4 className="text-xl font-bold mb-4 text-center">Mi Deseo Inicial: <span className="text-purple-300">{initialDesire}</span></h4>
                {questionsAndAnswers.map((qa, idx) => (
                  <div key={idx} className="mb-4 p-3 border-b border-gray-800 last:border-b-0">
                    {/* Añadimos clases para identificar los elementos de pregunta y respuesta */}
                    <p className="font-semibold text-lg text-purple-200 question-text">P{idx + 1}: {qa.question}</p>
                    <p className="text-gray-300 ml-4 italic answer-text">R{idx + 1}: {qa.answer}</p>
                  </div>
                ))}
              </div>

              <button
                onClick={handlePrintPdf}
                className="w-full py-3 px-6 bg-gradient-to-r from-green-500 to-blue-500 text-white font-semibold rounded-lg shadow-md hover:from-green-600 hover:to-blue-600 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isProcessing}
              >
                {isProcessing ? 'Generando PDF...' : 'Imprimir Respuestas (PDF)'}
              </button>
            </div>
          )}
        </div>
      </main>

      <footer className="relative z-10 text-center py-6 text-xs text-gray-600 mt-auto">
        © {new Date().getFullYear()} Gus • Aguiar | En misión de elevar y transformar.
      </footer>
    </div>
  );
};

export default SevenWhys;